/**
 * Job progress bar component
 * Visual progress indicator for jobs
 */

import { Progress, Space, Typography, Tooltip } from 'antd';
import type { JobStatus } from '@/types/job';
import { formatNumber } from '@/utils/formatters';

const { Text } = Typography;

interface JobProgressBarProps {
  completed: number;
  failed?: number;
  total: number;
  status: JobStatus;
  showText?: boolean;
  showPercentage?: boolean;
  size?: 'small' | 'default' | 'large';
}

export function JobProgressBar({
  completed,
  failed = 0,
  total,
  status,
  showText = false,
  showPercentage = true,
  size = 'default',
}: JobProgressBarProps) {
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const failedPercent = total > 0 ? Math.round((failed / total) * 100) : 0;

  const getProgressStatus = () => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'exception';
      case 'running':
        return 'active';
      default:
        return 'normal';
    }
  };

  // For jobs with failures, show stacked progress
  if (failed > 0 && total > 0) {
    const successPercent = Math.round(((completed - failed) / total) * 100);

    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Tooltip
          title={
            <div>
              <div>Completed: {formatNumber(completed - failed)} ({successPercent}%)</div>
              <div>Failed: {formatNumber(failed)} ({failedPercent}%)</div>
              <div>Remaining: {formatNumber(total - completed)} ({100 - percent}%)</div>
            </div>
          }
        >
          <Progress
            percent={percent}
            success={{ percent: successPercent, strokeColor: '#52c41a' }}
            strokeColor="#ff4d4f"
            size={size}
            status={getProgressStatus()}
            showInfo={showPercentage}
          />
        </Tooltip>
        {showText && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatNumber(completed)}/{formatNumber(total)} cells
            </Text>
            {failed > 0 && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {formatNumber(failed)} failed
              </Text>
            )}
          </div>
        )}
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Progress
        percent={percent}
        size={size}
        status={getProgressStatus()}
        showInfo={showPercentage}
        format={(val) => `${val}%`}
      />
      {showText && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatNumber(completed)}/{formatNumber(total)} cells
        </Text>
      )}
    </Space>
  );
}

/**
 * Compact progress indicator
 */
export function JobProgressCompact({
  completed,
  total,
  status,
}: {
  completed: number;
  total: number;
  status: JobStatus;
}) {
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Tooltip title={`${formatNumber(completed)}/${formatNumber(total)} cells (${percent}%)`}>
      <Progress
        type="circle"
        percent={percent}
        size={32}
        status={
          status === 'failed'
            ? 'exception'
            : status === 'completed'
            ? 'success'
            : 'normal'
        }
        format={(val) => (
          <span style={{ fontSize: 10 }}>{val}%</span>
        )}
      />
    </Tooltip>
  );
}

/**
 * Linear mini progress
 */
export function JobProgressMini({
  completed,
  total,
  status,
}: {
  completed: number;
  total: number;
  status: JobStatus;
}) {
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div style={{ width: 100 }}>
      <Progress
        percent={percent}
        size="small"
        showInfo={false}
        status={
          status === 'failed'
            ? 'exception'
            : status === 'completed'
            ? 'success'
            : status === 'running'
            ? 'active'
            : 'normal'
        }
      />
    </div>
  );
}

export default JobProgressBar;
